import React, { useContext, useEffect, useState } from "react";
import ICatalog_Item from "../../../models/ICatalog_Item";
import IDraftOrderItem from "../../../models/IDraftOrderItem";
import IImageSizeEnum from "../../../models/IImageSizeEnum";
import IKitDetails from "../../../models/IKitDetails";
import IOrder from "../../../models/IOrder";
import IConnection_Token from "../../../models/IConnection_Token";
import OwButton from "../../shared/OwButton/OwButton";
import OwLabel from "../../shared/OwLabel/OwLabel";
import OwS3ImageViewer from "../../shared/OwS3ImageViewer/OwS3ImageViewer";
import { KitOptionSelector } from "../KitOptionSelector/KitOptionSelector";
import "./EcommerceListView.scss";
import IAttachment from "../../../models/IAttachment";
import OwS3DownloadLink from "../../shared/OwS3DownloadLink/OwS3DownloadLink";
import { NumericFormat } from "react-number-format";
import { KitBasicListDetail } from "../KitBasicListDetail/KitBasicListDetail";
import { Socket } from "socket.io-client";
import UIService from "../../../services/UIService";
import { PricePerUnit } from "../../shared/PricePerUnit/PricePerUnit";
import OrderEntryService from "../../../services/OrderEntryService";
import IDiscount from "../../../models/IDiscount";
import UserService from "../../../services/UserService";
import { QtyOnHandValue } from "../../shared/QtyOnHandValue/QtyOnHandValue";
import { UomSetSelectOption } from "../../shared/UomSetSelectOption/UomSetSelectOption";
import IUnit_Of_Measurement_Set from "../../../models/IUnit_Of_Measurement_Set";
import { DescriptionExpander } from "../../shared/DescriptionExpander/DescriptionExpander";
import { KitBuilderModal } from "../KitBuilderModal/KitBuilderModal";
import IKit_Builder_Data from "../../../models/IKit_Builder_Data";
import { ProcessOrderEntryItemFn } from "../../../models/OrderEntryTypes";
import EndpointsEnum from "../../../models/EndpointsEnum";
import { OrderItemOptions } from "@components/shared/OrderItemOptions/OrderItemOptions";
import { useForm } from "react-hook-form";
import { ReactHookInput } from "@components/shared/ReactHookInput/ReactHookInput";
import INewOrder_Item_Option from "@models/INewOrder_Item_Option";
import { useAppContextNonNull } from "@contexts/AppContext";
import { ProductGroupSelector } from "@components/shared/ProductGroupSelector/ProductGroupSelector";
import { IAddBulkItems } from "@models/IAddBulkItems";
import UiContext from "@contexts/UiContext";
import EcommerceViewModeEnum from "../../../models/EcommerceViewModeEnum";
import CatalogService from "@services/CatalogService";
import SupplierService from "@services/SupplierService";
import { getPackItemOrderQty } from "@services/CatalogItemService";
import CurrenciesEnum from "@models/CurrenciesEnum";
import EcommerceCardFieldEnum from "@models/EcommerceCardFieldEnum";
import EcommerceCardViewService, { ICardViewSettings } from "@services/EcommerceCardViewService";

type Props = {
    catalogItem: ICatalog_Item;
    priceGroupName: string;
    addToCart: ProcessOrderEntryItemFn;
    openDetailView: (item: ICatalog_Item) => void;
    isReadOnly: boolean;
    order: IOrder;
    // The logged-in vendor's SUPPLIER_VENDOR token (price_discount_one/two). Used as the final
    // source for price calculation, since the catalog order may not carry supplierVendorToken.
    supplierVendorToken?: IConnection_Token;
    socket: Socket;
    isCategoryView?: boolean;
    viewMode?: EcommerceViewModeEnum;
    // Unsaved cardView settings (e.g. the settings-screen preview); wins over the supplier model's settings
    cardViewOverride?: ICardViewSettings;
};

const EcommerceListView = (props: Props) => {
    const { supplierModel, userModel, vendorModel } = useAppContextNonNull();
    const uiContext = useContext(UiContext);

    // Pricing reads order.supplierVendorToken; in ecomm the catalog order often doesn't carry it, so
    // defer to the order's token initially but prefer the explicitly passed-in token as the final source.
    const pricingOrder: IOrder = {
        ...props.order,
        supplierVendorToken: props.supplierVendorToken ?? props.order?.supplierVendorToken,
    };

    const isProductGroup = !!props.catalogItem.is_product_group;

    const [kitOptions, setKitOptions] = useState<IKitDetails[]>([]);
    const [resetQty, setResetQty] = useState(false);
    const [closeOptions, setCloseOptions] = useState(false);
    const [selectedSet, setSelectedSet] = useState<IUnit_Of_Measurement_Set | undefined>(props.catalogItem.Base_Unit);
    const [selectedItemOptions, setSelectedItemOptions] = useState<Partial<INewOrder_Item_Option>[]>([]);

    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        watch,
    } = useForm({
        defaultValues: {
            qty: 1,
        },
    });

    const qty = watch("qty");

    const qtyStep = OrderEntryService.getQtyStepValue(supplierModel?.qty_precision, props.catalogItem?.unit_incr_qty);

    const resetQtyCallbackFn = () => {
        setResetQty(false);
    };

    // The discount that applies to this item for the current vendor/price group, if the supplier uses discounts.
    // Shared by the regular add path and the kit builder path so both send the same discount to the cart.
    const getDiscountForCart = (): IDiscount | undefined => {
        if (!supplierModel.has_discounts) {
            return undefined;
        }

        return OrderEntryService.getDiscountedPriceForCatalogItem(props.catalogItem, supplierModel, pricingOrder).discount;
    };

    const addToCart = (e?: React.MouseEvent<HTMLButtonElement>) => {
        e?.stopPropagation();
        e?.preventDefault();

        const catalogItem = props.catalogItem;

        const discount = getDiscountForCart();

        // Buyer enters packs; store/ship in units. For non-price-per-pack items this multiplies
        // by pack_qty (matching standard order entry); otherwise getPackItemOrderQty returns qty unchanged.
        const orderItem: IDraftOrderItem = {
            ...catalogItem,
            qty: getPackItemOrderQty(catalogItem, qty),
            discount_id: discount?.id,
        };

        props.addToCart(orderItem, {
            kitDetails: kitOptions,
            discount,
            uomSet: selectedSet,
            shouldAutoSave: true,
            itemOptions: selectedItemOptions,
        });

        setCloseOptions(true);
        setResetQty(true);
    };

    const closeOptionsCallback = () => {
        setCloseOptions(false);
    };

    const addProductGroupItemsToCart = (items: IAddBulkItems[]) => {
        for (const bulkItem of items) {
            const variantCatalogItem = bulkItem.item.Catalog_Item;

            if (!variantCatalogItem) {
                continue;
            }

            const draftItem: IDraftOrderItem = {
                ...variantCatalogItem,
                qty: Number(bulkItem.item.qty) || 0,
            };

            const itemOptions = bulkItem.itemOptionValues?.map((v) => ({
                item_option_id: v.item_option_id,
                value: v.value,
            }));

            props.addToCart(draftItem, {
                kitDetails: bulkItem.kitDetails,
                shouldAutoSave: true,
                itemOptions,
            });
        }
    };

    const handleQtyBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        let value = Number(e.target.value);

        console.log(
            "handleQtyBlur - raw value:",
            e.target.value,
            "parsed:",
            value,
            "qtyStep:",
            qtyStep,
            "precision:",
            supplierModel?.qty_precision
        );

        if (isNaN(value) || value < qtyStep) {
            value = qtyStep;
        }

        // Round to supplier's qty_precision
        const precision = supplierModel?.qty_precision ?? 0;
        const multiplier = Math.pow(10, precision);
        value = Math.round(value * multiplier) / multiplier;

        console.log("handleQtyBlur - after rounding:", value);

        const qtyDetail = OrderEntryService.doInventoryValidation(
            props.catalogItem,
            value,
            props.order.Order_Items,
            supplierModel,
            props.order,
            0,
            selectedSet
        );

        console.log("handleQtyBlur - after validation:", qtyDetail.qty);

        setValue("qty", qtyDetail.qty);
    };

    const incrementQty = (e: React.MouseEvent<any>) => {
        e.stopPropagation();
        e.preventDefault();

        const step = qtyStep;
        const _qty = qty + step;

        const qtyDetail = OrderEntryService.doInventoryValidation(
            props.catalogItem,
            _qty,
            props.order.Order_Items || [],
            supplierModel,
            props.order,
            0,
            selectedSet
        );

        setValue("qty", qtyDetail.qty);
    };

    const decrementQty = (e: React.MouseEvent<any>) => {
        e.stopPropagation();
        e.preventDefault();

        const step = qtyStep;
        const _qty = qty - step;

        const qtyDetail = OrderEntryService.doInventoryValidation(
            props.catalogItem,
            _qty,
            props.order.Order_Items,
            supplierModel,
            props.order,
            0,
            selectedSet
        );

        if (qty - step >= step) {
            setValue("qty", qtyDetail.qty);
        }
    };

    const updateKitOptionSelection = (options: { id: number; qty: number }[]) => {
        setKitOptions(options);
    };

    const disableAddToCart = () => {
        let addToCartDisabled = false;

        if (props.catalogItem.min_order_qty && !props.catalogItem.is_kit) {
            if (qty < props.catalogItem.min_order_qty) {
                addToCartDisabled = true;
            }
        }

        if (props.catalogItem.max_order_qty) {
            if (qty > props.catalogItem.max_order_qty) {
                addToCartDisabled = true;
            }
        }

        if (props.catalogItem.has_inventory && !props.catalogItem.qty_on_hand && !props.catalogItem.allow_backorder) {
            addToCartDisabled = true;
        }

        if (props.catalogItem.is_kit && props.catalogItem.is_advanced_kit && !props.catalogItem.is_kit_builder) {
            let hasOneOption = false;
            let hasAllMinOrderQty = true;

            for (const option of kitOptions) {
                if (Number(option.qty) > 0) {
                    hasOneOption = true;
                }

                if (props.catalogItem.min_order_qty && hasOneOption) {
                    if (Number(option.qty) > 0 && Number(option.qty) < Number(props.catalogItem.min_order_qty)) {
                        hasAllMinOrderQty = false;
                    }
                }
            }

            if (props.catalogItem.min_order_qty) {
                if (hasOneOption && !hasAllMinOrderQty) {
                    addToCartDisabled = true;
                } else {
                    if (!hasOneOption) {
                        addToCartDisabled = true;
                    }
                }
            } else {
                if (!hasOneOption) {
                    addToCartDisabled = true;
                }
            }
        }

        return addToCartDisabled;
    };

    useEffect(() => {
        const qtyDetail = OrderEntryService.doInventoryValidation(
            props.catalogItem,
            qty,
            props.order.Order_Items || [],
            supplierModel,
            props.order,
            0,
            selectedSet
        );

        setValue("qty", qtyDetail.qty);
    }, [selectedSet]);

    const selectedSetFn = (set: IUnit_Of_Measurement_Set) => {
        setSelectedSet(set);
    };

    const getAttachmentDownloadLink = (catalogItem: ICatalog_Item) => {
        if (catalogItem && catalogItem.Attachments && catalogItem.file_attachment_one_id) {
            let attachmentItem: IAttachment | null = null;

            for (const attachment of catalogItem.Attachments) {
                if (attachment.id === catalogItem.file_attachment_one_id) {
                    attachmentItem = attachment;
                }
            }

            if (attachmentItem) {
                return (
                    <div className={"d-flex"}>
                        <OwS3DownloadLink
                            attachment={attachmentItem}
                            leftIconName={"fad fa-download"}
                            title={"download"}
                            isReadOnly={true}
                        />
                    </div>
                );
            } else {
                return null;
            }
        } else {
            return null;
        }
    };

    const viewDetailPage = () => {
        return;
        // props.openDetailView(props.catalogItem); // TBD
    };

    const showKitBuilderModal = () => {};

    const itemPrice = OrderEntryService.getDiscountedPriceForCatalogItem(props.catalogItem, supplierModel, pricingOrder, selectedSet);

    const addToCartDisabled = disableAddToCart();

    let hasInventoryAvailable = true;

    if (props.catalogItem.has_inventory) {
        if (!props.catalogItem.qty_on_hand) {
            if (!props.catalogItem.allow_backorder) {
                hasInventoryAvailable = false;
            }
        }
    }

    const getUom = () => {
        if (selectedSet) {
            return selectedSet?.unit_name || "";
        } else {
            return props.catalogItem.Unit_Of_Measurement?.name || "";
        }
    };

    const uom = getUom().toLowerCase();

    const getMsrpValue = () => {
        if (props.catalogItem.msrp) {
            let msrp = props.catalogItem.msrp || 0;

            if (selectedSet) {
                msrp = msrp * (selectedSet?.conversion_factor || 1);
            }

            return msrp;
        } else {
            return null;
        }
    };

    const onSubmit = (data: any) => {
        addToCart();
    };

    const selectedItemOptionsFn = (options: Partial<INewOrder_Item_Option>[]) => {
        setSelectedItemOptions(options);
    };

    const msrp = getMsrpValue();

    let showMaxOrderQty = true;

    if (props.catalogItem.is_kit && props.catalogItem.is_advanced_kit) {
        showMaxOrderQty = false;
    }

    let showCursorOnHover = true;

    if (supplierModel.ecomm_enable_item_listing_page) {
        showCursorOnHover = true;
    }

    const showInventoryDetails = UserService.showInventoryDetails(supplierModel, userModel);

    const hidePricing = UserService.hidePricing(supplierModel, userModel, vendorModel);

    const qtyOnHand = props.catalogItem.qty_on_hand || 0;

    const uomSetQtyConversion = OrderEntryService.getUomSetQty(qty || 1, props.catalogItem?.Base_Unit, selectedSet);

    let currency = props.order?.Catalog?.Currency ? props.order.Catalog.Currency : undefined;

    if (props.order?.Currency) {
        currency = props.order.Currency;
    }

    const prefix = currency
        ? OrderEntryService.getCurrencySymbol(currency.name)
        : CatalogService.getCurrencyFromId(vendorModel.Catalog?.currency_id || CurrenciesEnum.USD);

    const cardViewSettings = props.cardViewOverride ?? SupplierService.getSupplierSettings(supplierModel)?.screens?.eCommerce?.cardView;

    const { fieldOrder, visibility } = EcommerceCardViewService.normalizeCardView(cardViewSettings);

    // Whole-view suppressions skip the slot entirely (no reserved height), unlike per-item
    // guards below which render an empty slot so cards in a row stay aligned.
    const descriptionsSuppressed = supplierModel.guid === "4c78604f-cc40-41f4-b23e-06c96cd67fb3";

    const suppressedFields = new Set<EcommerceCardFieldEnum>([
        ...(descriptionsSuppressed ? [EcommerceCardFieldEnum.SHORT_DESCRIPTION, EcommerceCardFieldEnum.LONG_DESCRIPTION] : []),
        ...(props.isCategoryView ? [EcommerceCardFieldEnum.CATEGORY] : []),
        ...(hidePricing ? [EcommerceCardFieldEnum.PRICE, EcommerceCardFieldEnum.MSRP] : []),
        // Customer par is a par-level-supplier feature; the API omits `customer_par_qty` without the flag.
        ...(!supplierModel.has_par_levels ? [EcommerceCardFieldEnum.PAR] : []),
    ]);

    const fieldRenderers: Record<EcommerceCardFieldEnum, () => React.ReactNode> = {
        [EcommerceCardFieldEnum.NAME]: () => <div className={"item-name"}>{props.catalogItem.name}</div>,
        [EcommerceCardFieldEnum.SKU]: () =>
            props.catalogItem.sku !== props.catalogItem.name ? <div className={"text-muted sku"}>{props.catalogItem.sku}</div> : null,
        [EcommerceCardFieldEnum.MSRP]: () =>
            !isProductGroup && !props.catalogItem.is_kit && msrp ? (
                <div>
                    <OwLabel title={"msrp"} className={"me-2"} />
                    <NumericFormat value={msrp} displayType={"text"} decimalScale={2} fixedDecimalScale={true} prefix={prefix} />
                </div>
            ) : null,
        // The ordering customer's par (what their shelf should hold) — a pre-sell ordering aid.
        // null/absent = no par for this customer/item, so the slot stays empty.
        [EcommerceCardFieldEnum.PAR]: () =>
            props.catalogItem.customer_par_qty != null ? (
                <div className={"par-block"}>
                    <OwLabel title={"par_level"} postText={":"} className={"me-2"} />
                    <span className={"fw-semibold"}>{props.catalogItem.customer_par_qty}</span>
                </div>
            ) : null,
        [EcommerceCardFieldEnum.PRICE]: () =>
            !isProductGroup && !props.catalogItem.is_kit ? (
                props.priceGroupName.length ? (
                    <div className={"price-block"}>
                        <div className={"price"}>
                            <PricePerUnit
                                priceDetail={itemPrice}
                                prefix={prefix}
                                isEcomView={true}
                                currency={currency}
                                baseCurrency={props.order.Catalog?.Currency}
                                exchangeRate={props.order.exchange_rate}
                            />
                            {uom.length ? (
                                <>
                                    <span className={"ms-2 me-2"}>/</span>
                                    {uom}
                                </>
                            ) : null}
                        </div>
                    </div>
                ) : (
                    "--"
                )
            ) : null,
        [EcommerceCardFieldEnum.SHORT_DESCRIPTION]: () =>
            (props.catalogItem.short_description?.length || 0) > 0 ? <div>{props.catalogItem.short_description}</div> : null,
        [EcommerceCardFieldEnum.LONG_DESCRIPTION]: () => <DescriptionExpander catalogItem={props.catalogItem} />,
        [EcommerceCardFieldEnum.CATEGORY]: () =>
            props.catalogItem.Category ? <div className={"text-primary"}>{props.catalogItem.Category.name}</div> : null,
        [EcommerceCardFieldEnum.ACTIONS]: () => (
            <form onSubmit={handleSubmit(onSubmit)}>
                {isProductGroup || (props.catalogItem.is_kit && props.catalogItem.has_kit_base_options) ? null : (
                    <div className={props.isReadOnly ? "d-none" : "qty-wrapper"}>
                        <div className={"qty-change"}>
                            <div className={"input-group"}>
                                <OwButton
                                    centerIconClass={"fas fa-minus"}
                                    fn={decrementQty}
                                    disabled={!hasInventoryAvailable}
                                    disableDblClickPrevention={true}
                                />
                                <ReactHookInput
                                    defaultClassName={"form-control"}
                                    type={"number"}
                                    name={"qty"}
                                    onBlur={handleQtyBlur}
                                    disablePrecisionCheck={true}
                                    step={qtyStep}
                                    disabled={!hasInventoryAvailable}
                                    onWheel={UIService.stopScrollWheel}
                                    register={register("qty", {
                                        required: true,
                                        min: {
                                            value: qtyStep,
                                            message: `Quantity must be at least ${qtyStep}`,
                                        },
                                    })}
                                />
                                <OwButton
                                    centerIconClass={"fas fa-plus"}
                                    fn={incrementQty}
                                    disabled={!hasInventoryAvailable}
                                    disableDblClickPrevention={true}
                                />
                            </div>
                        </div>
                    </div>
                )}

                <div className={"text-center"}>
                    {props.catalogItem.has_pack_qty ? (
                        <div>
                            <OwLabel title={"total_qty"} postText={":"} className={"me-2"} />
                            {(props.catalogItem.pack_qty || 1) * qty}
                        </div>
                    ) : null}
                </div>

                <div>
                    {props.catalogItem.min_order_qty && !props.catalogItem.is_kit ? (
                        <div className={"text-small text-danger text-center"}>
                            <OwLabel title={"min_order_qty"} postText={":"} /> {props.catalogItem.min_order_qty}
                        </div>
                    ) : null}
                </div>

                <div>
                    {props.catalogItem.max_order_qty && showMaxOrderQty ? (
                        <div className={"text-small text-danger text-center"}>
                            <OwLabel title={"max_order_qty"} postText={":"} /> {props.catalogItem.max_order_qty}
                        </div>
                    ) : null}
                </div>

                {!isProductGroup && props.catalogItem.Base_Unit ? (
                    <div className={"uom-set-wrapper d-flex justify-content-center mt-2 " + (props.isReadOnly ? "d-none" : "")}>
                        <div className={"w-80"}>
                            <UomSetSelectOption item={props.catalogItem} selectedOption={selectedSetFn} currentOption={selectedSet} />
                        </div>
                    </div>
                ) : null}

                {uomSetQtyConversion ? (
                    <div className={"text-center"}>
                        {uomSetQtyConversion} {props.catalogItem?.Base_Unit?.unit_abbr}
                    </div>
                ) : null}

                {/* Kit builder items pick their options inside the KitBuilderModal instead. */}
                {!isProductGroup && !props.catalogItem.is_kit_builder && props.catalogItem.Options ? (
                    <div className={"mt-2"}>
                        <OrderItemOptions
                            catalogItem={props.catalogItem}
                            selectedItemOptionsFn={selectedItemOptionsFn}
                            flexDirection={"flex-column"}
                        />
                    </div>
                ) : null}

                {isProductGroup ? (
                    <div className={"mt-2"}>
                        <ProductGroupSelector
                            supplierModel={supplierModel}
                            catalogItem={props.catalogItem}
                            order={props.order}
                            notifyFn={uiContext.notifyFn}
                            socket={props.socket}
                            warehouses={[]}
                            addItems={addProductGroupItemsToCart}
                            orientation={"vertical"}
                        />
                    </div>
                ) : null}

                <div className={"action-btns mt-2 " + (isProductGroup || props.isReadOnly ? "d-none" : "")}>
                    <div className={"btn-group"}>
                        {/*<OwButton title={"detail_view"} fn={this.openDetailView} />*/}
                        {props.catalogItem?.is_kit_builder ? (
                            <KitBuilderModal
                                catalogItem={props.catalogItem}
                                showItemOptions={true}
                                addToCartOrOrder={(
                                    kitBuilderOptions: IKit_Builder_Data,
                                    itemOptions?: Partial<INewOrder_Item_Option>[]
                                ) => {
                                    const discount = getDiscountForCart();

                                    // TODO allow the user to select the quantity of the kit?
                                    props.addToCart(
                                        { ...props.catalogItem, qty: 1, discount_id: discount?.id },
                                        {
                                            kitBuilderOptions,
                                            itemOptions,
                                            uomSet: selectedSet,
                                            discount,
                                            shouldAutoSave: true,
                                        }
                                    );
                                }}
                                disableSelection={props.isReadOnly || addToCartDisabled}
                                saveEndpointReturn={EndpointsEnum.CartOnAddItem}
                            />
                        ) : (
                            <div className={"mt-2"}>
                                <OwButton
                                    fn={addToCart}
                                    title={"add_to_cart"}
                                    disabled={props.isReadOnly || addToCartDisabled || !!errors.qty}
                                />
                            </div>
                        )}
                    </div>
                </div>

                {!hasInventoryAvailable ? (
                    <div className="mt-2 mb-2 text-center ">
                        <OwLabel
                            title={"no_inventory_available"}
                            leftIconClass={"fas fa-exclamation-triangle"}
                            className={"text-warning"}
                        />
                    </div>
                ) : null}
            </form>
        ),
    };

    const viewModeClass = props.viewMode === EcommerceViewModeEnum.LIST ? "view-list" : "view-card";

    return (
        <div className={`EcommerceListView col ${viewModeClass}`}>
            <div
                className={`EcommerceListViewWrapper ${showCursorOnHover ? "show-cursor-on-hover" : ""}`}
                onClick={viewDetailPage}
                data-id={"ecomm-list-view"}>
                <div className={"image " + (props.catalogItem.Attachments?.length ? "has-image" : "")}>
                    <OwS3ImageViewer
                        catalogItem={props.catalogItem}
                        isMicroView={false}
                        imageIndex={IImageSizeEnum.SMALL}
                        className={""}
                        includeAllImages={true}
                        hideEmptyImage={true}
                    />
                    {props.catalogItem.Attachments?.length ? (
                        ""
                    ) : (
                        <div className={"empty-image"}>
                            <span className="fa-stack">
                                <i className="fa fa-image fa-stack-1x text-muted"></i>
                                <i className="fa fa-ban fa-stack-2x text-muted"></i>
                            </span>
                        </div>
                    )}
                </div>

                <div className={"ecommerce-list-view-contents"}>
                    {fieldOrder.map((field) =>
                        visibility[field] && !suppressedFields.has(field) ? (
                            <div key={field} className={`field-slot field-slot--${field}`}>
                                {fieldRenderers[field]()}
                            </div>
                        ) : null
                    )}
                    {(isProductGroup || props.catalogItem.is_kit) &&
                    props.catalogItem.is_advanced_kit &&
                    !props.catalogItem.is_kit_builder ? (
                        <KitOptionSelector
                            kitCatalogItem={props.catalogItem}
                            order={props.order}
                            userModel={userModel}
                            supplierModel={supplierModel}
                            vendorModel={vendorModel}
                            updateItemSelections={updateKitOptionSelection}
                            resetQty={resetQty}
                            resetQtyCallbackFn={resetQtyCallbackFn}
                            closeOptions={closeOptions}
                            closeOptionsCallbackFn={closeOptionsCallback}
                        />
                    ) : null}

                    <div className={"name-sku"}>
                        <div>
                            {props.catalogItem.has_pack_qty && props.catalogItem.pack_qty ? (
                                <div className={"mt-2"}>
                                    <OwLabel title={"pack_qty"} postText={":"} /> {props.catalogItem.pack_qty}
                                </div>
                            ) : null}
                        </div>
                    </div>

                    {/* <div className={"spec-file"}>
                        <FilesDownloadSection catalogItem={props.catalogItem} readOnly={true} className={"text-small mb-2"} />
                    </div> */}

                    {supplierModel.ecomm_show_qoh_on_card && props.catalogItem.has_inventory ? (
                        <div className={"qoh-block d-flex justify-content-center mt-auto"}>
                            <OwLabel title={"qoh"} postText={":"} className={"me-2"} />
                            <QtyOnHandValue catalogItem={props.catalogItem} />
                        </div>
                    ) : null}
                    {props.catalogItem.is_kit && !props.catalogItem.is_advanced_kit ? (
                        <KitBasicListDetail kitCatalogItem={props.catalogItem} />
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default EcommerceListView;
